import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { loadStripeTerminal } from "@stripe/terminal-js/pure";
import type { Reader, Terminal } from "@stripe/terminal-js";
import { toast } from "sonner";

interface AppContextType {
  stripeConnected: boolean;
  locationId?: string;
  stripeAccountLoading: boolean;
  refreshStripeAccount: () => Promise<void>;
  isInitializing: boolean;
  initError: string | null;
  isDiscovering: boolean;
  isConnecting: boolean;
  isProcessing: boolean;
  readers: Reader[];
  selectedReaderId: string;
  setSelectedReaderId: (id: string) => void;
  connectedReader: Reader | null;
  discoverReaders: () => Promise<void>;
  connectReader: () => Promise<void>;
  disconnectReader: () => Promise<void>;
  collectPayment: (params: {
    paymentId: string;
    onPaymentIntentCreated?: (paymentIntentId: string) => void;
  }) => Promise<string | null>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const terminalRef = useRef<Terminal | null>(null);
  const [stripeConnected, setStripeConnected] = useState(false);
  const [locationId, setLocationId] = useState<string | undefined>();
  const [stripeAccountLoading, setStripeAccountLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [readers, setReaders] = useState<Reader[]>([]);
  const [selectedReaderId, setSelectedReaderId] = useState("");
  const [connectedReader, setConnectedReader] = useState<Reader | null>(null);

  const refreshStripeAccount = useCallback(async () => {
    setStripeAccountLoading(true);
    try {
      const response = await fetch("/api/stripe-account");
      if (response.ok) {
        const data = await response.json();
        setStripeConnected(!!data.stripeAccount);
        setLocationId(data.stripeAccount?.locationId);
      } else {
        setStripeConnected(false);
        setLocationId(undefined);
      }
    } catch (error) {
      console.error("Failed to fetch Stripe account:", error);
      setStripeConnected(false);
      setLocationId(undefined);
    } finally {
      setStripeAccountLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshStripeAccount();
  }, [refreshStripeAccount]);

  useEffect(() => {
    let cancelled = false;

    async function initTerminal() {
      setIsInitializing(true);
      setInitError(null);

      try {
        const StripeTerminal = await loadStripeTerminal();
        if (cancelled) return;

        if (!StripeTerminal) {
          throw new Error("Stripe Terminal SDK failed to load");
        }

        const terminal = StripeTerminal.create({
          onFetchConnectionToken: async () => {
            const response = await fetch("/api/stripe-terminal/connection-token", {
              method: "POST",
            });

            if (!response.ok) {
              const data = await response.json().catch(() => ({}));
              throw new Error(data.error || "Failed to fetch connection token");
            }

            const data = await response.json();
            return data.secret;
          },
          onUnexpectedReaderDisconnect: () => {
            if (cancelled) return;
            setConnectedReader(null);
            toast.error("Reader disconnected unexpectedly. Discover and connect again.");
          },
        });

        terminalRef.current = terminal;
      } catch (error) {
        if (!cancelled) {
          const message =
            error instanceof Error ? error.message : "Failed to initialize Terminal";
          setInitError(message);
        }
      } finally {
        if (!cancelled) {
          setIsInitializing(false);
        }
      }
    }

    initTerminal();

    return () => {
      cancelled = true;
      terminalRef.current?.disconnectReader();
      terminalRef.current = null;
    };
  }, []);

  const discoverReaders = useCallback(async () => {
    const terminal = terminalRef.current;
    if (!terminal) {
      toast.error("Terminal is not ready");
      return;
    }

    setIsDiscovering(true);
    try {
      const config = locationId
        ? { simulated: false as const, location: locationId }
        : { simulated: true as const };

      const discoverResult = await terminal.discoverReaders(config);

      if ("error" in discoverResult && discoverResult.error) {
        throw new Error(discoverResult.error.message);
      }

      const discovered = discoverResult.discoveredReaders ?? [];
      setReaders(discovered);

      if (discovered.length > 0) {
        setSelectedReaderId(discovered[0]!.id);
      }

      if (discovered.length === 0) {
        toast.error("No readers found");
      } else {
        toast.success(`${discovered.length} reader(s) found`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to discover readers";
      toast.error(message);
    } finally {
      setIsDiscovering(false);
    }
  }, [locationId]);

  const connectReader = useCallback(async () => {
    const terminal = terminalRef.current;
    if (!terminal) {
      toast.error("Terminal is not ready");
      return;
    }

    const reader = readers.find((r) => r.id === selectedReaderId);
    if (!reader) {
      toast.error("Please select a reader");
      return;
    }

    setIsConnecting(true);
    try {
      const connectResult = await terminal.connectReader(reader);

      if ("error" in connectResult && connectResult.error) {
        throw new Error(connectResult.error.message);
      }

      setConnectedReader(connectResult.reader);
      toast.success(`Connected to ${connectResult.reader.label}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to connect to reader";
      toast.error(message);
    } finally {
      setIsConnecting(false);
    }
  }, [readers, selectedReaderId]);

  const disconnectReader = useCallback(async () => {
    const terminal = terminalRef.current;
    if (!terminal) return;

    try {
      await terminal.disconnectReader();
      setConnectedReader(null);
      toast.success("Reader disconnected");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to disconnect reader";
      toast.error(message);
    }
  }, []);

  const collectPayment = useCallback(
    async ({
      paymentId,
      onPaymentIntentCreated,
    }: {
      paymentId: string;
      onPaymentIntentCreated?: (paymentIntentId: string) => void;
    }) => {
      const terminal = terminalRef.current;
      if (!terminal || !connectedReader) {
        toast.error("Not connected to a reader");
        return null;
      }

      setIsProcessing(true);
      try {
        const intentResponse = await fetch("/api/stripe-terminal/payment-intent", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ paymentId }),
        });

        if (!intentResponse.ok) {
          const data = await intentResponse.json().catch(() => ({}));
          throw new Error(data.error || "Failed to create payment intent");
        }

        const intentData = await intentResponse.json();
        const clientSecret = intentData.clientSecret as string;
        const paymentIntentId = intentData.paymentIntentId as string | undefined;

        if (paymentIntentId) {
          onPaymentIntentCreated?.(paymentIntentId);
        }

        const collectResult = await terminal.collectPaymentMethod(clientSecret);

        if ("error" in collectResult && collectResult.error) {
          throw new Error(collectResult.error.message);
        }

        const processResult = await terminal.processPayment(collectResult.paymentIntent);

        if ("error" in processResult && processResult.error) {
          throw new Error(processResult.error.message);
        }

        toast.success("Payment collected successfully");
        return processResult.paymentIntent.id;
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to collect payment";
        toast.error(message);
        return null;
      } finally {
        setIsProcessing(false);
      }
    },
    [connectedReader]
  );

  const value = useMemo(
    () => ({
      stripeConnected,
      locationId,
      stripeAccountLoading,
      refreshStripeAccount,
      isInitializing,
      initError,
      isDiscovering,
      isConnecting,
      isProcessing,
      readers,
      selectedReaderId,
      setSelectedReaderId,
      connectedReader,
      discoverReaders,
      connectReader,
      disconnectReader,
      collectPayment,
    }),
    [
      stripeConnected,
      locationId,
      stripeAccountLoading,
      refreshStripeAccount,
      isInitializing,
      initError,
      isDiscovering,
      isConnecting,
      isProcessing,
      readers,
      selectedReaderId,
      connectedReader,
      discoverReaders,
      connectReader,
      disconnectReader,
      collectPayment,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}
